"use client";

import Link from "next/link";
import Image from "next/image";
import { NavBtn } from "../navButton/NavBtn";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./style.css";

interface FeaturedPost {
  id: string;
  title: string;
  summary: string;

  author: string;
  author_slug: string;
  author_username: string;

  created_at: string;
  date: string;

  title_slug: string;
  url: string;

  image: string;
  category_name?: string; // ✅ add
  category_slug?: string;

  tags?: string; // ✅ changed
  featured?: boolean;
  private?: boolean;
}

interface Props {
  featuredPosts: FeaturedPost[];
}

export default function FeaturedSlider({ featuredPosts }: Props) {
  const [mounted, setMounted] = useState(false);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = mounted ? resolvedTheme === "dark" : false;
  const [showText, setShowText] = useState(true);

  useEffect(() => {
    setMounted(true);
  }, []);
  if (!featuredPosts || featuredPosts.length === 0) return null;

  return (
    <section className="featured-slider">
      <div className="featured-slider__container card-container">
        {/* Swiper Wrapper */}
        <div style={{ position: "relative" }}>
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            slidesPerView={1}
            loop
            speed={800}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            navigation={{
              prevEl: ".featured-prev-btn",
              nextEl: ".featured-next-btn",
            }}
            pagination={{ clickable: true }}
            onSlideChange={() => {
              setShowText(false);

              setTimeout(() => {
                setShowText(true);
              }, 400); // ⏱️ 0.10 sec delay
            }}
            style={{borderRadius: "10px"}}
          >
            {featuredPosts.map((post) => (
              <SwiperSlide key={post.id}>
                <div className="featured-card">
                  <picture className="featured-card__media">
                    <Image
                      className="featured-card__img"
                      src={post.image}
                      alt={post.title}
                      width={4000}
                      height={3000}
                      priority
                    />
                  </picture>

                  {/* ✅ div instead of Link — fixes nested <a> error */}
                  <div
                    className={`featured-card__content ${
                      showText ? "text-visible" : "text-hidden"
                    }`}
                  >
                    {/* Meta + title + excerpt → clickable to post */}
                    <Link
                      href={`/${post.url || post.title_slug}`}
                      className="featured-card__inner-link"
                    >
                      <div
                        className="featured-card__meta"
                        style={{
                          fontFamily: "manrope, sans-serif",
                          fontSize: "16px",
                          fontWeight: 900,
                          color: "#fff",
                        }}
                      >
                        <span>By {post.author_username || post.author}</span>
                        <span className="dot-separator">•</span>
                        <time>
                          {post.created_at
                            ? (() => {
                                const d = new Date(post.created_at);
                                const date = d.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                });
                                const time = d.toLocaleTimeString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                                return `${date} ${time}`;
                              })()
                            : post.date}
                        </time>
                      </div>

                      <h2 className="featured-card__title">{post.title}</h2>

                      {post.summary && (
                        <p className="featured-card__excerpt mb-3">
                          {post.summary}
                        </p>
                      )}
                    </Link>

                    {/* ✅ Category badge AFTER summary — separate <a>, no nesting */}
                    {post.category_name && post.category_slug && (
                      <Link
                        href={`/${post.category_slug}`}
                        className="featured-card__category-badge"
                      >
                        {post.category_name}
                      </Link>
                    )}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* LEFT BUTTON */}
          <NavBtn
            direction="prev"
            hover={hoverPrev}
            className="featured-prev-btn swiper-nav-btn"
            onMouseEnter={() => setHoverPrev(true)}
            onMouseLeave={() => setHoverPrev(false)}
          />

          <NavBtn
            direction="next"
            hover={hoverNext}
            className="featured-next-btn swiper-nav-btn"
            onMouseEnter={() => setHoverNext(true)}
            onMouseLeave={() => setHoverNext(false)}
          />
        </div>
      </div>
    </section>
  );
}
